<template>
  <div class="page-wrapper">
    <!-- Background orbs -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- ── Navbar ── -->
    <nav class="navbar">
      <router-link to="/" class="nav-brand">
        <img
          src="https://res.cloudinary.com/dldd8ucby/image/upload/v1587167979/RA/WhatsApp_Image_2020-04-16_at_10.51.15_AM.jpg"
          alt="G1BRA Logo"
          class="nav-logo"
        />
        <span class="nav-title">G1BRA</span>
      </router-link>

      <button class="nav-hamburger" @click="toggle">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      <div class="nav-links" :class="{ 'nav-links--open': open }">
        <router-link to="/login" class="nav-link">Login</router-link>
      </div>
    </nav>

    <!-- ── Main Content ── -->
    <main class="content">

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Registered Candidates</h1>
          <p class="page-subtitle">{{ users.length }} candidate{{ users.length !== 1 ? 's' : '' }} registered</p>
        </div>
        <vue-excel-xlsx
          :data="users"
          :columns="columns"
          filename="Examination List 2026"
          :sheetname="'sheetname'"
          class="export-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export Excel
        </vue-excel-xlsx>
      </div>

      <!-- Stats Row -->
      <div class="stats-row" v-if="users.length">
        <div class="stat-card">
          <span class="stat-label">Total Candidates</span>
          <span class="stat-value">{{ users.length }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">Churches</span>
          <span class="stat-value">{{ uniqueChurches }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">Ranks</span>
          <span class="stat-value">{{ uniqueRanks }}</span>
        </div>
      </div>

      <!-- Table Card -->
      <div class="table-card">

        <!-- Toolbar -->
        <div class="table-toolbar">
          <div class="search-wrapper">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="search"
              type="text"
              class="search-input"
              placeholder="Search by name, church, rank…"
            />
            <button v-if="search" class="search-clear" @click="search = ''">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <span class="results-count">{{ filteredUsers.length }} result{{ filteredUsers.length !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="skeleton-rows">
          <div v-for="n in 6" :key="n" class="skeleton-row">
            <div class="skeleton-cell skeleton-cell--narrow"></div>
            <div class="skeleton-cell skeleton-cell--wide"></div>
            <div class="skeleton-cell skeleton-cell--wide"></div>
            <div class="skeleton-cell skeleton-cell--med"></div>
            <div class="skeleton-cell skeleton-cell--narrow"></div>
          </div>
        </div>

        <!-- Table -->
        <div v-else class="table-wrap">
          <table class="results-table">
            <thead>
              <tr>
                <th @click="sortBy('index')" class="th-sortable">
                  #
                  <span class="sort-arrow" :class="sortArrowClass('index')">▲</span>
                </th>
                <th @click="sortBy('first_name')" class="th-sortable">
                  First Name
                  <span class="sort-arrow" :class="sortArrowClass('first_name')">▲</span>
                </th>
                <th @click="sortBy('last_name')" class="th-sortable">
                  Last Name
                  <span class="sort-arrow" :class="sortArrowClass('last_name')">▲</span>
                </th>
                <th @click="sortBy('church')" class="th-sortable tw-hide-sm">
                  Church
                  <span class="sort-arrow" :class="sortArrowClass('church')">▲</span>
                </th>
                <th @click="sortBy('rank')" class="th-sortable tw-hide-sm">
                  Rank
                  <span class="sort-arrow" :class="sortArrowClass('rank')">▲</span>
                </th>
                <th class="tw-hide-xs">User ID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredUsers.length === 0">
                <td colspan="6" class="empty-row">No candidates match your search.</td>
              </tr>
              <tr
                v-for="(row, i) in sortedUsers"
                :key="i"
                class="data-row"
              >
                <td class="td-index">{{ i + 1 }}</td>
                <td class="td-name">{{ row.first_name }}</td>
                <td class="td-name">{{ row.last_name }}</td>
                <td class="td-meta tw-hide-sm">{{ row.church }}</td>
                <td class="td-meta tw-hide-sm">
                  <span class="rank-badge">{{ row.rank }}</span>
                </td>
                <td class="td-id tw-hide-xs">{{ row.num }}</td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </main>

    <vue-element-loading v-if="loading" :active="loading" :is-full-screen="true" />
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      users: [],
      open: false,
      loading: true,
      search: "",
      sortKey: "index",
      sortDir: 1,
      headers: [
        { text: "First Name", align: "start", sortable: false, value: "first_name" },
        { text: "Last Name",  value: "last_name" },
        { text: "Church",     value: "church" },
        { text: "Rank",       value: "rank" },
        { text: "User Id",    value: "num" },
      ],
      columns: [
        { label: "S/N",        field: "index" },
        { label: "First Name", field: "first_name" },
        { label: "Last Name",  field: "last_name" },
        { label: "Church",     field: "church" },
        { label: "Rank",       field: "rank" },
      ],
    };
  },
  computed: {
    filteredUsers() {
      if (!this.search) return this.users;
      const q = this.search.toLowerCase();
      return this.users.filter(r =>
        [r.first_name, r.last_name, r.church, r.rank]
          .filter(Boolean)
          .some(v => v.toLowerCase().includes(q))
      );
    },
    sortedUsers() {
      return [...this.filteredUsers].sort((a, b) => {
        const av = a[this.sortKey] ?? '';
        const bv = b[this.sortKey] ?? '';
        if (av < bv) return -1 * this.sortDir;
        if (av > bv) return  1 * this.sortDir;
        return 0;
      });
    },
    uniqueChurches() {
      return new Set(this.users.map(u => u.church).filter(Boolean)).size;
    },
    uniqueRanks() {
      return new Set(this.users.map(u => u.rank).filter(Boolean)).size;
    },
  },
  async mounted() {
    let res = await this.fetchUsers();
    let users = [];
    let i = 1;
    res.forEach((user) => {
      users.push({
        index:      i++,
        num:        user._id.toString(),
        first_name: user.first_name,
        last_name:  user.last_name,
        church:     user.church,
        rank:       user.rank,
      });
    });
    this.loading = false;
    this.users = users;
  },
  methods: {
    ...mapActions(["fetchUsers", "submit"]),
    toggle() { this.open = !this.open; },
    sortBy(key) {
      if (this.sortKey === key) { this.sortDir *= -1; }
      else { this.sortKey = key; this.sortDir = 1; }
    },
    sortArrowClass(key) {
      if (this.sortKey !== key) return 'sort-arrow--inactive';
      return this.sortDir === 1 ? 'sort-arrow--asc' : 'sort-arrow--desc';
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Jost:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; }

/* ── Page ── */
.page-wrapper {
  min-height: 100vh;
  background: #f4f5f9;
  font-family: 'Jost', sans-serif;
  color: #1a1a2e;
  position: relative;
}

.bg-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.07;
}
.bg-orb-1 { width: 600px; height: 600px; background: #c8a96e; top: -200px; right: -150px; }
.bg-orb-2 { width: 500px; height: 500px; background: #3a5f8a; bottom: -150px; left: -100px; }

/* ── Navbar ── */
.navbar {
  position: relative;
  z-index: 10;
  background: #0f1923;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  height: 64px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.25);
}
.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
}
.nav-logo {
  width: 36px; height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(200,169,110,0.4);
}
.nav-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #c8a96e;
  letter-spacing: 0.06em;
}
.nav-hamburger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  padding: 0.4rem;
}
.nav-hamburger svg { width: 20px; height: 20px; }
.nav-links { display: flex; align-items: center; gap: 1.5rem; }
.nav-link {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.65);
  text-decoration: none;
  transition: color 0.2s;
}
.nav-link:hover { color: #c8a96e; }

/* ── Content ── */
.content {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

/* ── Page Header ── */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.page-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.2rem;
}
.page-subtitle {
  font-size: 0.82rem;
  color: #8a8fa8;
  font-weight: 400;
  margin: 0;
}
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #2d7a4f, #3a9960);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 14px rgba(45,122,79,0.25);
  text-decoration: none;
}
.export-btn svg { width: 15px; height: 15px; }
.export-btn:hover {
  background: linear-gradient(135deg, #256640, #2f7d4e);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(45,122,79,0.3);
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border-top: 3px solid #c8a96e;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.stat-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8a8fa8;
}
.stat-value {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1;
}

/* ── Table Card ── */
.table-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  overflow: hidden;
}

/* ── Toolbar ── */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid #f0f2f8;
  flex-wrap: wrap;
}
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 360px;
}
.search-icon {
  position: absolute;
  left: 0.85rem;
  width: 15px; height: 15px;
  color: #b0b5cc;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 0.6rem 2.4rem 0.6rem 2.5rem;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  color: #1a1a2e;
  background: #f7f8fc;
  border: 1.5px solid #e2e5f0;
  border-radius: 9px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input:focus {
  border-color: #c8a96e;
  box-shadow: 0 0 0 3px rgba(200,169,110,0.1);
  background: #fff;
}
.search-input::placeholder { color: #b8bdce; }
.search-clear {
  position: absolute;
  right: 0.7rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #b0b5cc;
  display: flex;
  padding: 0.15rem;
}
.search-clear svg { width: 13px; height: 13px; }
.search-clear:hover { color: #6b7090; }
.results-count {
  font-size: 0.78rem;
  color: #8a8fa8;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Skeleton ── */
.skeleton-rows { padding: 0.5rem 0; }
.skeleton-row {
  display: flex;
  gap: 1rem;
  padding: 0.9rem 1.5rem;
  border-bottom: 1px solid #f0f2f8;
}
.skeleton-cell {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f2f8 25%, #e8eaf0 50%, #f0f2f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.skeleton-cell--wide   { width: 22%; }
.skeleton-cell--med    { width: 28%; }
.skeleton-cell--narrow { width: 12%; }
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Table ── */
.table-wrap { overflow-x: auto; }
.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.results-table thead tr {
  background: #f7f8fc;
  border-bottom: 2px solid #e8eaf2;
}
.results-table th {
  padding: 0.85rem 1.25rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7090;
  white-space: nowrap;
}
.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-sortable:hover { color: #1a1a2e; }
.sort-arrow {
  display: inline-block;
  font-size: 0.55rem;
  margin-left: 0.3rem;
  transition: transform 0.2s, opacity 0.2s;
}
.sort-arrow--inactive { opacity: 0.2; }
.sort-arrow--asc      { opacity: 1; transform: rotate(0deg); }
.sort-arrow--desc     { opacity: 1; transform: rotate(180deg); }

.data-row {
  border-bottom: 1px solid #f0f2f8;
  transition: background 0.15s;
}
.data-row:last-child { border-bottom: none; }
.data-row:hover { background: #fafbfd; }

.results-table td {
  padding: 0.85rem 1.25rem;
  color: #3a3f5c;
  vertical-align: middle;
}
.td-index { color: #b0b5cc; font-size: 0.78rem; font-weight: 600; }
.td-name  { font-weight: 500; color: #1a1a2e; text-transform: capitalize; }
.td-meta  { color: #6b7090; font-size: 0.85rem; text-transform: capitalize; }
.td-id    { color: #b0b5cc; font-size: 0.75rem; font-family: monospace; }

.rank-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  background: #f0f2f8;
  color: #4a4f6a;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

.empty-row {
  text-align: center;
  padding: 3rem 1rem !important;
  color: #b0b5cc;
  font-size: 0.9rem;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .stats-row { grid-template-columns: repeat(3, 1fr); }
  .tw-hide-sm { display: none; }
  .nav-hamburger { display: flex; }
  .nav-links {
    display: none;
    position: absolute;
    top: 64px; left: 0; right: 0;
    background: #0f1923;
    flex-direction: column;
    padding: 1rem 1.5rem;
    gap: 1rem;
    border-top: 1px solid rgba(255,255,255,0.08);
  }
  .nav-links--open { display: flex; }
}
@media (max-width: 480px) {
  .stats-row { grid-template-columns: 1fr 1fr; }
  .tw-hide-xs { display: none; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .export-btn { width: 100%; justify-content: center; }
  .table-toolbar { flex-direction: column; align-items: stretch; }
  .search-wrapper { max-width: 100%; }
}
</style>